/**
 * Sponsors sub-page — rendered at `?tab=more&view=sponsors` inside MorePage.
 * Reuses SponsorsBody from SponsorsBottomSheet for tier sections + POI-linked cards.
 */
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import type { SponsorTierGroup } from "@wondermaps/db";
import { VisitorPageHeader } from "./VisitorPageHeader";
import { SponsorsBody } from "./SponsorsBottomSheet";
import type { SponsorLogo } from "./sponsor-types";
import type { VisitorNotification } from "./notification-types";
import type { VisitorSearchParams } from "../../lib/visitor-search";

export interface SponsorsSubPageProps {
  notifications: VisitorNotification[];
  onBellClick: () => void;
  isTrayOpen: boolean;
  mapSlug: string;
  sponsors?: SponsorTierGroup[];
  onSponsorClick?: (sponsor: SponsorLogo, placement: string) => void;
  getPoiName?: (poiId: string) => string | undefined;
  onPoiClick?: (poiId: string) => void;
}

export function SponsorsSubPage({
  notifications,
  onBellClick,
  isTrayOpen,
  mapSlug,
  sponsors,
  onSponsorClick,
  getPoiName,
  onPoiClick,
}: SponsorsSubPageProps) {
  const { t } = useTranslation("map");
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as VisitorSearchParams;

  const handleBack = () => {
    void navigate({ to: ".", search: { ...search, view: undefined } });
  };

  return (
    <div
      className="absolute inset-0 z-10 bg-background overflow-y-auto"
      style={{ paddingBottom: "var(--bottom-nav-height, 0px)" }}
    >
      <VisitorPageHeader
        showBack
        onBack={handleBack}
        title={t("more.sponsorsTitle")}
        notifications={notifications}
        onBellClick={onBellClick}
        isTrayOpen={isTrayOpen}
        mapSlug={mapSlug}
      />
      {sponsors && sponsors.length > 0 ? (
        <SponsorsBody
          sponsors={sponsors}
          onSponsorClick={onSponsorClick}
          getPoiName={getPoiName}
          onPoiClick={onPoiClick}
          placement="sponsors_subpage"
        />
      ) : (
        <div className="px-4 py-8 text-center text-sm text-muted-foreground">
          {t("sponsors.empty", { defaultValue: "No sponsors yet." })}
        </div>
      )}
    </div>
  );
}
